import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Star, MessageCircle, Calendar, TrendingUp, 
  Heart, Lightbulb, AlertCircle, CheckCircle, Send,
  ChevronDown, ChevronUp, Smile, ThumbsUp
} from 'lucide-react';
import Navigation from '../components/Navigation';
import DailyInsight from '../components/DailyInsight';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ParentInsights = () => {
  const navigate = useNavigate();
  const { childId } = useParams();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  
  const [feedback, setFeedback] = useState({
    helpfulness: 0,
    accuracy: 0,
    clarity: 0,
    actionability: 0,
    whatResonated: '',
    whatWasMissing: '',
    questions: '',
    additionalComments: '',
    wantsCallback: false
  });

  const getParentId = () => {
    const user = localStorage.getItem('parent_user');
    if (user) return JSON.parse(user).id;
    return null;
  };

  useEffect(() => {
    const token = localStorage.getItem('parent_token');
    if (!token) {
      navigate('/parent/login');
      return;
    }
    loadInsights();
  }, [navigate, childId]);

  const loadInsights = async () => {
    try {
      const parentId = getParentId();
      const response = await axios.get(
        `${BACKEND_URL}/api/parent/${parentId}/insights?limit=20`
      );
      
      // Filter by child if childId provided
      let filteredInsights = response.data.insights;
      if (childId) {
        filteredInsights = filteredInsights.filter(i => i.child_id === childId);
      }
      
      setInsights(filteredInsights);
    } catch (error) {
      console.error('Error loading insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const submitFeedback = async () => {
    if (!selectedInsight) return;
    
    setFeedbackSubmitting(true);
    try {
      const parentId = getParentId();
      
      await axios.post(
        `${BACKEND_URL}/api/feedback/insight`,
        null,
        {
          params: {
            insight_id: selectedInsight.id,
            parent_id: parentId,
            child_id: selectedInsight.child_id,
            helpfulness_rating: feedback.helpfulness,
            accuracy_rating: feedback.accuracy,
            clarity_rating: feedback.clarity,
            actionability_rating: feedback.actionability,
            what_resonated: feedback.whatResonated,
            what_was_missing: feedback.whatWasMissing,
            questions: feedback.questions,
            additional_comments: feedback.additionalComments,
            wants_callback: feedback.wantsCallback,
            preferred_callback_time: ''
          }
        }
      );
      
      setFeedbackSuccess(true);
      setTimeout(() => {
        setShowFeedbackForm(false);
        setFeedbackSuccess(false);
        setFeedback({
          helpfulness: 0, accuracy: 0, clarity: 0, actionability: 0,
          whatResonated: '', whatWasMissing: '', questions: '', additionalComments: '',
          wantsCallback: false
        });
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const StarRating = ({ value, onChange, label }) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="focus:outline-none"
          >
            <Star
              className={`w-8 h-8 transition-colors ${
                star <= value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );

  const getInsightTypeLabel = (type) => {
    switch (type) {
      case 'daily': return { label: 'Daily Insight', color: 'bg-blue-100 text-blue-700' };
      case 'weekly': return { label: 'Weekly Summary', color: 'bg-purple-100 text-purple-700' };
      case 'monthly': return { label: 'Monthly Report', color: 'bg-green-100 text-green-700' };
      default: return { label: 'Insight', color: 'bg-gray-100 text-gray-700' };
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/parent/dashboard')}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            
            <h1 className="text-2xl font-bold text-gray-900">Session Insights</h1>
            <p className="text-gray-600">View insights from your child&apos;s sessions</p>
          </div>

          {insights.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Lightbulb className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Insights Yet</h3>
                <p className="text-gray-600">
                  Insights will appear here after your child&apos;s sessions are reviewed.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {insights.map((insight) => {
                const typeInfo = getInsightTypeLabel(insight.type);
                const isExpanded = selectedInsight?.id === insight.id;
                const insightData = insight.insight || insight.summary || {};
                
                return (
                  <Card 
                    key={insight.id}
                    className={`transition-all ${isExpanded ? 'ring-2 ring-blue-500' : ''}`}
                  >
                    <CardContent className="p-6">
                      {/* Header */}
                      <div 
                        className="flex justify-between items-start cursor-pointer"
                        onClick={() => setSelectedInsight(isExpanded ? null : insight)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${typeInfo.color}`}>
                              {typeInfo.label}
                            </span>
                            <span className="text-sm text-gray-500">
                              {insight.child_name}
                            </span>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {insightData.header || insightData.todays_focus || 'Session Insight'}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            {formatDate(insight.created_at)}
                          </p>
                        </div>
                        <button className="p-2 hover:bg-gray-100 rounded-full">
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="mt-6 pt-6 border-t">
                          {/* Daily Insight Component */}
                          <DailyInsight 
                            childName={insight.child_name}
                            date={insight.created_at}
                            focus={insightData.todays_focus || insightData.header}
                            snapshot={insightData.snapshot}
                            brightSpots={insightData.bright_spots || []}
                            whatWeNoticed={insightData.what_we_noticed ? [insightData.what_we_noticed] : []}
                            homeConnection={Array.isArray(insightData.home_connection) 
                              ? insightData.home_connection.join(' ') 
                              : insightData.home_connection}
                            tomorrowSeed={insightData.tomorrows_seed}
                          />

                          {/* Parent Summary (if available) */}
                          {insight.parent_summary && (
                            <div className="mt-4 mb-6 bg-green-50 p-4 rounded-lg">
                              <h4 className="font-semibold text-green-800 mb-2">Summary for You</h4>
                              <p className="text-green-700 whitespace-pre-line">{insight.parent_summary}</p>
                            </div>
                          )}

                          {/* Feedback Section */}
                          <div className="mt-6 pt-6 border-t">
                            {!showFeedbackForm ? (
                              <div className="flex items-center justify-between">
                                <p className="text-sm text-gray-600">Was this insight helpful?</p>
                                <Button
                                  onClick={() => setShowFeedbackForm(true)}
                                  variant="outline"
                                  className="border-blue-300 text-blue-600"
                                >
                                  <ThumbsUp className="w-4 h-4 mr-2" />
                                  Give Feedback
                                </Button>
                              </div>
                            ) : feedbackSuccess ? (
                              <div className="text-center py-4">
                                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                                <p className="text-green-700 font-semibold">Thank you for your feedback!</p>
                              </div>
                            ) : (
                              <div className="bg-gray-50 p-6 rounded-lg">
                                <h4 className="font-semibold text-gray-900 mb-4">Share Your Feedback</h4>
                                
                                <div className="grid md:grid-cols-2 gap-4">
                                  <StarRating
                                    label="How helpful was this insight?"
                                    value={feedback.helpfulness}
                                    onChange={(v) => setFeedback({...feedback, helpfulness: v})}
                                  />
                                  <StarRating
                                    label="How accurate did it feel?"
                                    value={feedback.accuracy}
                                    onChange={(v) => setFeedback({...feedback, accuracy: v})}
                                  />
                                  <StarRating
                                    label="How clear was it?"
                                    value={feedback.clarity}
                                    onChange={(v) => setFeedback({...feedback, clarity: v})}
                                  />
                                  <StarRating
                                    label="How actionable was it?"
                                    value={feedback.actionability}
                                    onChange={(v) => setFeedback({...feedback, actionability: v})}
                                  />
                                </div>

                                <div className="mt-4 space-y-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      What resonated with you?
                                    </label>
                                    <Textarea
                                      value={feedback.whatResonated}
                                      onChange={(e) => setFeedback({...feedback, whatResonated: e.target.value})}
                                      placeholder="Share what felt true or meaningful..."
                                      className="h-20"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Any questions for us?
                                    </label>
                                    <Textarea
                                      value={feedback.questions}
                                      onChange={(e) => setFeedback({...feedback, questions: e.target.value})}
                                      placeholder="We'd love to answer any questions..."
                                      className="h-20"
                                    />
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={feedback.wantsCallback}
                                      onChange={(e) => setFeedback({...feedback, wantsCallback: e.target.checked})}
                                      className="w-4 h-4"
                                    />
                                    <span className="text-sm text-gray-700">
                                      I&apos;d like a callback to discuss this further
                                    </span>
                                  </div>
                                </div>

                                <div className="mt-6 flex gap-3">
                                  <Button
                                    variant="outline"
                                    onClick={() => setShowFeedbackForm(false)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    onClick={submitFeedback}
                                    disabled={feedbackSubmitting || !feedback.helpfulness}
                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                  >
                                    {feedbackSubmitting ? 'Submitting...' : 'Submit Feedback'}
                                    <Send className="w-4 h-4 ml-2" />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentInsights;
